import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import React from 'react';

import { generateCorrectSlug } from '../../helpers/generate-correct-slug';
import { isBrowser } from '../../helpers/is-browser';
import { calculatePrefixPath } from '../../helpers/rendered-page-creation/calculate-prefixed-path';
import ImageWrapper from '../image-wrapper/Image-wrapper';
import LinkButton from '../ui/link-button/link-button';
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import { IMeetingPlaceLinkToAnyPage } from '@/lib/fragments/meetingPlaceLinkToAnyPage';
import LinkWrapper from '../link-wrapper/link-wrapper';
import IframeWrapper from '../iframe-wrapper/iframe-wrapper';
import {
  allowedHostsInRichText,
  isBookingSubdomain,
  LIVLI_FAMILY_LOGIN_MP_ID_BY_URL,
} from '@/helpers/utils';
import {
  useTenantConfig,
  useUrlSubDirectoryDictionary,
} from '@/providers/ConfigProvider';
import { getUrlSubdirectory } from '@/helpers/rendered-page-creation/get-url-subdirectories';
import Image from 'next/image';
import Button from '../ui/button/button';
import { initiateLogin } from '@/helpers/api/user-profile-api';
import { useParams, usePathname } from 'next/navigation';
import { useUser } from '@/providers/UserProvider';
interface RichTextProps {
  richText: any;
  characterLimitCount?: number;
  assetClass?: string;
  onBookingClick?: () => void;
}

export const RichText = ({
  richText,
  characterLimitCount,
  assetClass,
  onBookingClick,
}: RichTextProps) => {
  const config = useTenantConfig();
  const isLivliFamily = config?.NEXT_PUBLIC_IS_MP_LIVLI_FAMILY === 'true';
  const isTempLoginEnabled = config?.NEXT_PUBLIC_TEMP_LOGIN_ENABLED === 'true';
  const isIcimLoginEnabled = config?.NEXT_PUBLIC_ICIM_LOGIN_ENABLED === 'true';
  const isLoginEnabled = isIcimLoginEnabled || isTempLoginEnabled;
  const prefix = config?.GATSBY_SITE_PATH_PREFIX || '';
  const urlSubdirectories = useUrlSubDirectoryDictionary();
  const pathname = usePathname();
  const { locale: siteLocale } = useParams<{ locale: string }>();
  const { isLoggedIn } = useUser();

  let totalCharacterCount = 0;

  const shouldRenderContent = () => {
    return !characterLimitCount || totalCharacterCount < characterLimitCount;
  };

  // Create asset map from links for quick lookup
  const assetMap = React.useMemo(() => {
    const map = new Map();
    if (richText?.links?.assets?.block) {
      richText.links.assets.block.forEach((asset: any) => {
        // Use the asset's sys.id if available, otherwise use a fallback
        const id = asset?.sys?.id || asset?.__typename;
        map.set(id, asset);
      });
    }
    return map;
  }, [richText]);
  // Create entry map from links for quick lookup
  const entryMap = React.useMemo(() => {
    const map = new Map();
    if (richText?.links?.entries?.block) {
      richText.links.entries.block.forEach((entry: any) => {
        const id = entry?.sys?.id || entry?.__typename;
        map.set(id, entry);
      });
    }
    if (richText?.links?.entries?.hyperlink) {
      richText.links.entries.hyperlink.forEach((entry: any) => {
        const id = entry?.sys?.id || entry?.__typename;
        map.set(id, entry);
      });
    }
    return map;
  }, [richText]);

  const handleLogin = async (mpId: string, locale: string = siteLocale) => {
    try {
      const destination = isTempLoginEnabled
        ? 'livli-family-success'
        : 'profile';
      const path = [prefix, locale, destination].filter(Boolean).join('/');
      const currentPath = `/${path}${
        isTempLoginEnabled ? '' : `?mpid=${mpId}`
      }`;
      if (isLoggedIn && isBrowser) {
        window.location.href = currentPath;
      } else {
        localStorage.setItem('current_path', currentPath);
        await initiateLogin(locale, mpId, config, pathname ? pathname : '/');
      }
    } catch (error) {
      console.error('[LoginRegister] Login initiation failed:', error);
    }
    // Note: setIsLoading(false) not called on success — page redirects away
  };

  const options: Options = {
    renderText: (text) => {
      if (characterLimitCount) {
        const remainingCharacters = characterLimitCount - totalCharacterCount;
        if (remainingCharacters > 0) {
          const slicedText = text.slice(0, remainingCharacters);
          totalCharacterCount += slicedText.length;
          const formattedText = slicedText
            .split('\n')
            .flatMap((t, i) => [i > 0 && <br key={i} />, t]);
          // add three dots at the end.
          if (totalCharacterCount >= characterLimitCount) {
            formattedText.push(<span key="ellipsis">...</span>);
          }
          return formattedText;
        }
        return [];
      } else {
        return text.split('\n').flatMap((t, i) => [i > 0 && <br key={i} />, t]);
      }
    },
    renderNode: {
      [BLOCKS.HEADING_2]: (_, children) => {
        return (
          <h2 className="mb-4 font-title text-headingm text-text-default lg:mb-8 lg:text-headingxl">
            {children}
          </h2>
        );
      },
      [BLOCKS.HEADING_3]: (_, children) => {
        return (
          <h3 className="mb-4 font-title text-headings text-text-default lg:mb-4 lg:text-headingm">
            {children}
          </h3>
        );
      },
      [BLOCKS.HEADING_4]: (_, children) => {
        return (
          <h4 className="mb-4 font-title text-headings text-text-default lg:text-headings">
            {children}
          </h4>
        );
      },
      [BLOCKS.PARAGRAPH]: (_, children) =>
        children != '' ? <p>{children}</p> : '',
      [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
        if (!shouldRenderContent()) return null;

        // Get the asset ID from the node
        const assetId = node?.data?.target?.sys?.id;

        // Look up the asset in the assetMap
        const asset = assetId ? assetMap.get(assetId) : null;

        if (!asset) {
          console.warn('Asset not found:', assetId);
          return null;
        }

        if (asset?.url?.includes('.mp4')) {
          return (
            <div className="flex w-full items-center justify-center">
              <div className="w-full">
                <video className="mb-8 w-full" controls src={asset?.url} />
              </div>
            </div>
          );
        } else
          return (
            <ImageWrapper
              url={asset?.url || ''}
              alt={asset?.description || asset?.title || ''}
              className={`mb-8 transform-gpu rounded-xl md:rounded-xxl ${assetClass}`}
              imgClassName={`mb-8 transform-gpu rounded-xl md:rounded-xxl ${assetClass}`}
              // width={asset?.width ? parseInt(asset.width) : undefined}
              width={asset?.width ? 1000 : undefined}
              height={asset?.height ? parseInt(asset.height) : undefined}
            />
          );
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
        if (!shouldRenderContent()) return null;

        const entryId = node?.data?.target?.sys?.id;

        // Look up the entry in the entryMap
        const entry = entryId ? entryMap.get(entryId) : null;

        if (!entry) {
          console.warn('Entry not found:', entryId);
          return null;
        }

        const target = node?.data?.target?.sys;
        const type = entry?.__typename;
        const isExternalLink = entry?.externalLink?.length > 0;
        const isInternalLink = entry?.internalLink?.slug?.length > 0;
        const image = entry?.mediaField?.jsonBynderAsset?.originalUrl;
        if (
          type === 'BlockLink' &&
          (entry?.linkText?.length > 0 || image.length > 0) &&
          (entry?.externalLink?.length > 0 ||
            entry?.internalLink?.slug?.length > 0)
        ) {
          const openInNewWindow = entry?.openInNewWindow;
          let url = '';
          if (isExternalLink) {
            url = entry?.externalLink;
          } else if (isInternalLink) {
            let pageSlug;
            const nodeSlug = entry?.internalLink.slug;
            const subType = entry?.internalLink.__typename;
            if (subType === 'LibraryPopupModal') {
              url =
                (isBrowser &&
                  calculatePrefixPath(
                    window.location.pathname + nodeSlug,
                    config?.GATSBY_SITE_PATH_PREFIX,
                  )) ||
                '';
              // Generate url using pageSlug if url is empty
              if (!url) {
                pageSlug = nodeSlug;
                const urlSubdirectory = getUrlSubdirectory(
                  urlSubdirectories,
                  entry?.internalLink?.sys?.locale,
                  subType,
                );
                url = generateCorrectSlug(
                  {
                    // customizedSlug: target?.internalLink.customizedSlug,
                    // node_locale: target?.internalLink.node_locale,
                    sys: { locale: entry?.internalLink?.sys?.locale },
                    slug: pageSlug,
                    __typename: entry?.internalLink.__typename,
                  } as IMeetingPlaceLinkToAnyPage,
                  config?.GATSBY_SITE_PATH_PREFIX,
                  urlSubdirectory,
                );
              }
            } else {
              if (subType === 'MeetingPlacePageHome') {
                pageSlug = '/';
              } else {
                pageSlug = nodeSlug;
              }
              const urlSubdirectory = getUrlSubdirectory(
                urlSubdirectories,
                entry?.internalLink?.sys?.locale,
                subType,
              );
              url = generateCorrectSlug(
                {
                  // sys: entry?.internalLink?.sys?.locale,
                  sys: { locale: entry?.internalLink?.sys?.locale },
                  slug: pageSlug,
                  __typename: entry?.internalLink?.__typename,
                } as IMeetingPlaceLinkToAnyPage,
                config?.GATSBY_SITE_PATH_PREFIX,
                urlSubdirectory,
              );
            }
          }
          return image ? (
            isInternalLink ? (
              <LinkWrapper
                className="mb-8"
                data={entry.internalLink}
                target={openInNewWindow ? '_blank' : ''}
              >
                {/* <ImageWrapper
                  url={image || ''}
                  alt={image.description ?? ''}
                  imgClassName={`mb-8 transform-gpu rounded-xl md:rounded-xxl`}
                  className="mb-8 transform-gpu rounded-xl md:rounded-xxl"
                  width={image.width ? parseInt(image.width) : 1000}
                  height={image.height ? parseInt(image.height) : 100}
                /> */}
                <div className="mb-8 transform-gpu rounded-xl md:rounded-xxl inline-block">
                  <Image
                    src={image || ''}
                    alt={entry?.mediaField?.altText || 'alt text not found'}
                    width={image.width ? parseInt(image.width) : 1000}
                    height={image.height ? parseInt(image.height) : 1000}
                    style={{ width: '100%', height: 'auto' }}
                    className="rounded-xl md:rounded-xxl object-contain"
                  />
                </div>
              </LinkWrapper>
            ) : (
              isExternalLink && (
                <a
                  href={url}
                  target={openInNewWindow ? '_blank' : ''}
                  rel="noreferrer"
                >
                  {/* <ImageWrapper
                    url={image || ''}
                    alt={image ?? ''}
                    className="mb-8 transform-gpu rounded-xl md:rounded-xxl"
                    imgClassName={`mb-8 transform-gpu rounded-xl md:rounded-xxl`}
                    width={image?.width ? parseInt(image.width) : 1000}
                    height={image?.height ? parseInt(image.height) : 100}
                  /> */}
                  <div className="mb-8 transform-gpu rounded-xl md:rounded-xxl inline-block">
                    <Image
                      src={image || ''}
                      alt={entry?.mediaField?.altText || 'alt text not found'}
                      width={image.width ? parseInt(image.width) : 1000}
                      height={image.height ? parseInt(image.height) : 1000}
                      style={{ width: '100%', height: 'auto' }}
                      className="rounded-xl md:rounded-xxl object-contain"
                    />
                  </div>
                </a>
              )
            )
          ) : (
            url &&
              (isLivliFamily && isBookingSubdomain(url) && isExternalLink ? (
                <Button className={`mb-8 mt-4`} onClick={onBookingClick}>
                  {entry?.linkText}
                </Button>
              ) : url in LIVLI_FAMILY_LOGIN_MP_ID_BY_URL && isLoginEnabled ? (
                <Button
                  variant="primary"
                  className="text-center mb-8"
                  onClick={() =>
                    handleLogin(
                      LIVLI_FAMILY_LOGIN_MP_ID_BY_URL[url],
                      entry?.sys?.locale,
                    )
                  }
                >
                  {entry?.linkText}
                </Button>
              ) : (
                <LinkButton
                  className="mb-8 mr-4"
                  to={url}
                  openInNewTab={openInNewWindow}
                  isExternalLink={isExternalLink}
                >
                  {entry?.linkText}
                </LinkButton>
              ))
          );
        }
      },
      [INLINES.ENTRY_HYPERLINK]: (node: any, children) => {
        if (!shouldRenderContent()) return null;
        const entryId = node?.data?.target?.sys?.id;
        const entry = entryId ? entryMap.get(entryId) : null;

        if (!entry) {
          console.warn('Hyperlink entry not found:', entryId);
          // Return the text content instead of null to avoid breaking lists
          return <>{children}</>;
        }

        const type = entry?.__typename;
        const nodeSlug = entry?.slug;
        let pageSlug;

        if (type === 'MeetingPlacePageHome') {
          pageSlug = '/';
        } else {
          pageSlug = nodeSlug;
        }

        return (
          <LinkWrapper
            data={{
              name: entry?.name,
              sys: { locale: entry?.sys?.locale },
              slug: pageSlug,
              __typename: entry?.__typename,
            }}
            className="mb-4"
          >
            {children}
          </LinkWrapper>
        );
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        if (!shouldRenderContent()) return null;
        const assetId = node?.data?.target?.sys?.id;

        let asset = null;
        if (richText?.links?.assets?.hyperlink) {
          asset = richText.links.assets.hyperlink
            ?.filter(Boolean)
            ?.find((a: any) => (a.sys?.id || a.__typename) === assetId);
        }

        if (asset?.url) {
          return (
            <a href={asset.url} target="_blank" rel="noreferrer">
              {children}
            </a>
          );
        }
        // Return children instead of null to avoid breaking lists
        return <>{children}</>;
      },
      [INLINES.HYPERLINK]: (node: any, children) => {
        if (!shouldRenderContent()) return null;
        const isAllowedHost = allowedHostsInRichText.some((host) =>
          node.data.uri.includes(host),
        );

        if (node.data.uri.includes('vimeo') && isAllowedHost) {
          return (
            <IframeWrapper>
              <iframe
                title={node.content[0]?.value}
                src={node.data.uri}
                className="border-none"
                allowFullScreen
              ></iframe>
            </IframeWrapper>
          );
        } else if (node.data.uri.includes('youtube') && isAllowedHost) {
          const videoEmbedUrl = node.data.uri.includes('youtube.com/embed')
            ? node.data.uri
            : `https://www.youtube.com/embed/${new URL(
                node.data.uri,
              ).searchParams.get('v')}`;
          return (
            <IframeWrapper>
              <iframe
                title={node.content[0]?.value}
                src={videoEmbedUrl}
                allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                className="border-none"
                allowFullScreen
              ></iframe>
            </IframeWrapper>
          );
        } else {
          return (
            <a
              className="mb-4"
              href={node.data.uri}
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          );
        }
      },
      [BLOCKS.UL_LIST]: (_, children) => {
        if (!shouldRenderContent()) return null;
        const listItems = React.Children.toArray(children);
        if (listItems && listItems.filter(Boolean).length > 0) {
          return <ul className="">{listItems}</ul>;
        }
        return null;
      },
      [BLOCKS.OL_LIST]: (_, children) => {
        if (!shouldRenderContent()) return null;
        const listItems = React.Children.toArray(children);
        if (listItems && listItems.filter(Boolean).length > 0) {
          return <ol className="">{listItems}</ol>;
        }
        return null;
      },
      [BLOCKS.LIST_ITEM]: (_, children) => {
        if (!shouldRenderContent()) return null;
        const content = React.Children.toArray(children);
        if (content && content.length > 0) {
          return <li>{content}</li>;
        }
        return null;
      },
      [BLOCKS.TABLE]: (_, children) => {
        return <table className="mb-8">{children}</table>;
      },
      [BLOCKS.TABLE_ROW]: (_, children) => {
        return <tr>{children}</tr>;
      },
      [BLOCKS.TABLE_HEADER_CELL]: (_, children) => {
        return <th className="border px-4 py-2 font-bold">{children}</th>;
      },
      [BLOCKS.TABLE_CELL]: (_, children) => {
        return <td className="border px-4 py-2">{children}</td>;
      },
    },
  };

  return (
    <div className="richText">
      {documentToReactComponents(richText.json, options)}
      <div className="h-px"></div>
    </div>
  );
};

